import React, { useState, useEffect } from 'react';
import {
	Card,
	Table,
	Button,
	Space,
	Modal,
	Form,
	DatePicker,
	InputNumber,
	Input,
	message,
	Tag,
	Tooltip,
	Row,
	Col,
	Typography,
	Select,
	Progress,
	Descriptions,
	List,
	Empty,
	Spin,
	Divider,
	Alert,
} from 'antd';
import {
	PlusOutlined,
	CalendarOutlined,
	ReloadOutlined,
	EyeOutlined,
	CheckCircleOutlined,
	ClockCircleOutlined,
	ScheduleOutlined,
	EditOutlined,
	DeleteOutlined,
	StopOutlined,
	BellOutlined,
	CheckOutlined,
	CloseOutlined,
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import locale from 'antd/locale/fr_FR';
import { ConfigProvider } from 'antd';
import api from '../services/api';
import { customersService, Customer } from '../services/customersService';
import equipmentsService, { Equipment } from '../services/equipmentsService';
import { maintenanceOfferService, MaintenanceOffer } from '../services/maintenanceOfferService';

dayjs.locale('fr');

const { Title, Text } = Typography;
const { Option } = Select;

interface ScheduledContract {
	id: number;
	customer_id: number;
	maintenance_offer_id?: number | null;
	equipment_count?: number;
	equipment_description: string | null;
	equipment_model: string | null;
	contract_type: 'scheduled' | 'on_demand';
	visits_total: number;
	visits_completed: number;
	visit_interval_months: number;
	next_visit_date: string | null;
	first_intervention_date: string;
	status: string;
	payment_status: string;
	price?: number;
	original_price?: number | null;
	discount_amount?: number | null;
	first_payment_amount?: number | null;
	second_payment_amount?: number | null;
	second_payment_status?: string | null;
	start_date: string;
	end_date: string;
	created_at: string;
	// Relations renvoyées par GET /api/contracts/scheduled
	customer?: {
		id: number;
		email?: string;
		first_name?: string;
		last_name?: string;
		phone?: string;
	} | null;
	offer?: MaintenanceOffer | null;
	// Conditions commerciales figées à la souscription
	offer_snapshot?: {
		title?: string;
		offer_type?: string;
		annual_discount_percentage?: number;
		includes_freon?: boolean;
		pricing_mode?: string;
		unit_price?: number;
	} | null;
}

interface ScheduledVisit {
	id: number;
	scheduled_date: string;
	status: string;
	type: string;
	completed_at: string | null;
}

interface UpcomingVisit {
	id: number;
	scheduled_date: string;
	customer_id: number;
	equipment_id: number;
	subscription_id: number;
	status: string;
	equipment_name?: string | null;
}

interface RenewalRequest {
	id: number;
	contractId: number;
	contractReference: string;
	customerId: number;
	customerName: string;
	customerEmail?: string;
	customerPhone?: string;
	isSubscription: boolean;
	equipmentDescription?: string;
	equipmentModel?: string;
	subscriptionStatus?: string;
	subscriptionEndDate?: string;
	requestedAt: string;
	message?: string;
}

const ScheduledContractsPage: React.FC = () => {
	const [contracts, setContracts] = useState<ScheduledContract[]>([]);
	const [upcomingVisits, setUpcomingVisits] = useState<UpcomingVisit[]>([]);
	const [customers, setCustomers] = useState<Customer[]>([]);
	const [equipments, setEquipments] = useState<Equipment[]>([]);
	const [annualOffers, setAnnualOffers] = useState<MaintenanceOffer[]>([]);
	const [loading, setLoading] = useState(false);
	const [loadingVisits, setLoadingVisits] = useState(false);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [isVisitsModalVisible, setIsVisitsModalVisible] = useState(false);
	const [selectedContract, setSelectedContract] = useState<ScheduledContract | null>(null);
	const [contractVisits, setContractVisits] = useState<ScheduledVisit[]>([]);
	const [loadingContractVisits, setLoadingContractVisits] = useState(false);
	const [renewalRequests, setRenewalRequests] = useState<RenewalRequest[]>([]);
	const [loadingRenewals, setLoadingRenewals] = useState(false);
	const [form] = Form.useForm();
	const watchedOfferId = Form.useWatch('maintenanceOfferId', form);
	const watchedEquipmentCount = Form.useWatch('equipmentCount', form) || 1;
	const selectedAnnualOffer = annualOffers.find(offer => offer.id === watchedOfferId);

	// Charger les contrats programmés
	const loadContracts = async () => {
		setLoading(true);
		try {
			const response = await api.get('/contracts/scheduled', { params: { limit: 1000 } });
			if (response.data.success) {
				setContracts(response.data.data);
			}
		} catch (error) {
			console.error('Erreur chargement contrats programmés:', error);
			message.error('Erreur lors du chargement des contrats');
		} finally {
			setLoading(false);
		}
	};

	// Charger les visites à venir
	const loadUpcomingVisits = async () => {
		setLoadingVisits(true);
		try {
			const response = await api.get('/contracts/upcoming-visits', { params: { days: 30 } });
			if (response.data.success) {
				setUpcomingVisits(response.data.data);
			}
		} catch (error) {
			console.error('Erreur chargement visites à venir:', error);
		} finally {
			setLoadingVisits(false);
		}
	};

	// Charger les clients
	const loadCustomers = async () => {
		try {
			const response = await customersService.getCustomers();
			setCustomers(response.data?.customers || []);
		} catch (error) {
			console.error('Erreur chargement clients:', error);
		}
	};

	// Charger les équipements
	const loadEquipments = async () => {
		try {
			const response = await equipmentsService.getAll();
			setEquipments(response.data?.data?.equipments || []);
		} catch (error) {
			console.error('Erreur chargement équipements:', error);
		}
	};

	const loadAnnualOffers = async () => {
		try {
			const offers = await maintenanceOfferService.getAll();
			setAnnualOffers(offers.filter(offer => offer.offer_type === 'annual' && offer.isActive));
		} catch (error) {
			console.error('Erreur chargement offres annuelles:', error);
			message.error('Erreur lors du chargement des offres annuelles');
		}
	};

	// Charger les demandes de renouvellement
	const loadRenewalRequests = async () => {
		setLoadingRenewals(true);
		try {
			const response = await api.get('/contracts/scheduled/renewal-requests');
			if (response.data.success) {
				setRenewalRequests(response.data.data || []);
			}
		} catch (error) {
			console.error('Erreur chargement demandes renouvellement:', error);
		} finally {
			setLoadingRenewals(false);
		}
	};

	// Traiter une demande de renouvellement
	const handleRenewalRequest = async (requestId: number, action: 'approve' | 'reject') => {
		try {
			const response = await api.post(`/contracts/scheduled/renewal-requests/${requestId}/process`, {
				action,
				notes: action === 'reject' ? 'Demande examinée par l\'équipe.' : undefined
			});
			
			if (response.data.success) {
				message.success(response.data.message);
				loadRenewalRequests(); // Recharger la liste
			}
		} catch (error: any) {
			console.error('Erreur traitement demande:', error);
			message.error(error.response?.data?.message || 'Erreur lors du traitement');
		}
	};

	// Charger les visites d'un contrat
	const loadContractVisits = async (contractId: number) => {
		setLoadingContractVisits(true);
		try {
			const response = await api.get(`/contracts/scheduled/${contractId}/visits`);
			if (response.data.success) {
				setContractVisits(response.data.data.visits);
			}
		} catch (error) {
			console.error('Erreur chargement visites:', error);
			message.error('Erreur lors du chargement des visites');
		} finally {
			setLoadingContractVisits(false);
		}
	};

	useEffect(() => {
		loadContracts();
		loadUpcomingVisits();
		loadCustomers();
		loadEquipments();
		loadAnnualOffers();
		loadRenewalRequests();
	}, []);

	// Créer un nouveau contrat
	const handleCreate = async (values: any) => {
		try {
			if (selectedContract) {
				// Mode modification
				const updatePayload = {
					equipment_description: values.equipment,
					equipment_model: values.model,
					visits_total: values.visitsTotal,
					visit_interval_months: values.visitIntervalMonths,
				};

				const response = await api.patch(`/subscriptions/${selectedContract.id}`, updatePayload);
				
				if (response.data.success) {
					message.success('Contrat modifié avec succès');
					setIsModalVisible(false);
					setSelectedContract(null);
					form.resetFields();
					loadContracts();
				}
			} else {
				// Mode création
				const payload = {
					customerId: values.customerId,
					maintenanceOfferId: values.maintenanceOfferId,
					equipmentCount: values.equipmentCount,
					equipment: values.equipment,
					model: values.model,
					firstInterventionDate: values.firstInterventionDate.format('YYYY-MM-DD'),
				};

				const response = await api.post('/contracts/scheduled', payload);
				
				if (response.data.success) {
					message.success(response.data.message);
					setIsModalVisible(false);
					form.resetFields();
					loadContracts();
					loadUpcomingVisits();
				}
			}
		} catch (error: any) {
			console.error('Erreur contrat:', error);
			message.error(error.response?.data?.message || 'Erreur lors de l\'opération');
		}
	};

	// Voir les visites d'un contrat
	const handleViewVisits = (contract: ScheduledContract) => {
		setSelectedContract(contract);
		loadContractVisits(contract.id);
		setIsVisitsModalVisible(true);
	};

	// Modifier un contrat
	const handleEdit = (contract: ScheduledContract) => {
		setSelectedContract(contract);
		form.setFieldsValue({
			customerId: contract.customer_id,
			maintenanceOfferId: contract.maintenance_offer_id,
			equipmentCount: contract.equipment_count || 1,
			equipment: contract.equipment_description,
			model: contract.equipment_model,
			visitsTotal: contract.visits_total,
			visitIntervalMonths: contract.visit_interval_months,
		});
		setIsModalVisible(true);
	};

	// Annuler un contrat
	const handleCancel = (contract: ScheduledContract) => {
		Modal.confirm({
			title: 'Annuler le contrat',
			content: `Êtes-vous sûr de vouloir annuler ce contrat ? Le client ne pourra plus bénéficier des visites restantes.`,
			okText: 'Annuler le contrat',
			okType: 'danger',
			cancelText: 'Retour',
			onOk: async () => {
				try {
					await api.patch(`/subscriptions/${contract.id}/cancel`);
					message.success('Contrat annulé avec succès');
					loadContracts();
					loadUpcomingVisits();
				} catch (error: any) {
					console.error('Erreur annulation contrat:', error);
					message.error(error.response?.data?.message || 'Erreur lors de l\'annulation');
				}
			},
		});
	};

	// Supprimer un contrat
	const handleDelete = (contract: ScheduledContract) => {
		Modal.confirm({
			title: 'Confirmer la suppression',
			content: `Êtes-vous sûr de vouloir supprimer ce contrat ? Cette action est irréversible.`,
			okText: 'Supprimer',
			okType: 'danger',
			cancelText: 'Annuler',
			onOk: async () => {
				try {
					await api.delete(`/subscriptions/${contract.id}`);
					message.success('Contrat supprimé avec succès');
					loadContracts();
					loadUpcomingVisits();
				} catch (error: any) {
					console.error('Erreur suppression contrat:', error);
					message.error(error.response?.data?.message || 'Erreur lors de la suppression');
				}
			},
		});
	};

	// Obtenir le nom du client
	const getCustomerName = (customerId: number) => {
		if (!customerId) return 'Client non spécifié';
		const customer: any = customers.find((c: any) => Number(c.user_id) === Number(customerId) || Number(c.id) === Number(customerId) || Number(c.user?.id) === Number(customerId));
		if (customer) {
			const name = `${customer.first_name || customer.user?.first_name || ''} ${customer.last_name || customer.user?.last_name || ''}`.trim();
			if (name) return name;
			if (customer.company_name) return customer.company_name;
		}
		return `Client #${customerId}`;
	};

	// Privilégie la relation `customer` renvoyée par l'API, sinon retombe sur
	// la liste des clients chargée séparément.
	const getContractCustomerName = (contract: any) => {
		if (!contract) return 'Client';
		if (contract.customer_name) return contract.customer_name;
		const joined = `${contract.customer?.first_name || ''} ${contract.customer?.last_name || ''}`.trim();
		if (joined) return joined;
		return getCustomerName(contract.customer_id);
	};

	// Obtenir le nom de l'équipement
	const getEquipmentName = (equipmentId: number) => {
		const equipment = equipments.find(e => e.id === equipmentId);
		return equipment ? `${equipment.brand || ''} ${equipment.model || ''}`.trim() : `Équipement #${equipmentId}`;
	};

	// Colonnes du tableau
	const columns: ColumnsType<ScheduledContract> = [
		{
			title: 'Client',
			key: 'customer',
			render: (_, record) => (
				<Space direction="vertical" size={0}>
					<Text strong>{getContractCustomerName(record)}</Text>
					{record.customer?.email && (
						<Text type="secondary" style={{ fontSize: 12 }}>{record.customer.email}</Text>
					)}
				</Space>
			),
		},
		{
			title: 'Offre annuelle',
			key: 'offer',
			render: (_, record) => {
				const title = record.offer_snapshot?.title || record.offer?.title;
				const discount = record.offer_snapshot?.annual_discount_percentage
					?? record.offer?.annual_discount_percentage
					?? 0;
				const includesFreon = record.offer_snapshot?.includes_freon ?? record.offer?.includes_freon;
				if (!title) {
					// Contrat historique créé avant l'unification des offres
					return <Text type="secondary">{record.equipment_description || 'Offre non rattachée'}</Text>;
				}
				return (
					<Space direction="vertical" size={2}>
						<Text strong>{title}</Text>
						<Text type="secondary" style={{ fontSize: 12 }}>
							{record.equipment_count || 1} équipement(s)
							{record.price !== undefined && record.price !== null
								? ` · ${Number(record.price).toLocaleString('fr-FR')} FCFA`
								: ''}
						</Text>
						<Space size={4}>
							{discount > 0 && <Tag color="green">−{discount}%</Tag>}
							{includesFreon && <Tag color="cyan">Fréon inclus</Tag>}
						</Space>
					</Space>
				);
			},
		},
		{
			title: 'Équipement',
			key: 'equipment',
			render: (_, record) => (
				<Space direction="vertical" size={0}>
					<Text>{record.equipment_description || '-'}</Text>
					{record.equipment_model && (
						<Text type="secondary" style={{ fontSize: 12 }}>{record.equipment_model}</Text>
					)}
				</Space>
			),
		},
		{
			title: 'Progression',
			key: 'progress',
			render: (_, record) => {
				const total = record.visits_total || 1;
				const completed = Math.min(record.visits_completed || 0, total);
				const percent = Math.min(100, Math.round((completed / total) * 100));
				return (
					<Space direction="vertical" size="small" style={{ width: '100%' }}>
						<Progress 
							percent={percent} 
							size="small"
							status={completed >= total ? 'success' : 'active'}
						/>
						<Text type="secondary" style={{ fontSize: 12 }}>
							{completed} / {total} visites
						</Text>
					</Space>
				);
			},
			width: 180,
		},
		{
			title: 'Intervalle',
			key: 'interval',
			render: (_, record) => (
				<Tag icon={<CalendarOutlined />}>
					{record.visit_interval_months} mois
				</Tag>
			),
			width: 100,
		},
		{
			title: 'Prochaine visite',
			key: 'next_visit',
			render: (_, record) => {
				if (!record.next_visit_date) {
					return <Tag color="blue">Terminé</Tag>;
				}
				const nextDate = dayjs(record.next_visit_date);
				const daysUntil = nextDate.diff(dayjs(), 'day');
				let color = 'blue';
				if (daysUntil < 0) color = 'red';
				else if (daysUntil <= 7) color = 'orange';
				
				return (
					<Tooltip title={`Dans ${daysUntil} jours`}>
						<Tag color={color} icon={<ScheduleOutlined />}>
							{nextDate.format('DD/MM/YYYY')}
						</Tag>
					</Tooltip>
				);
			},
			width: 150,
		},
		{
			title: 'Statut',
			key: 'status',
			render: (_, record) => {
				const statusColors: Record<string, string> = {
					pending_payment: 'gold',
					active: 'green',
					awaiting_second_payment: 'volcano',
					completed: 'blue',
					used: 'geekblue',
					expired: 'red',
					cancelled: 'default',
				};
				const statusLabels: Record<string, string> = {
					pending_payment: 'Paiement en attente',
					active: 'Actif',
					awaiting_second_payment: 'Solde 50 % en attente',
					completed: 'Terminé',
					used: 'Consommé',
					expired: 'Expiré',
					cancelled: 'Annulé',
				};
				return (
					<Tag color={statusColors[record.status] || 'default'}>
						{statusLabels[record.status] || record.status}
					</Tag>
				);
			},
			width: 100,
		},
		{
			title: 'Actions',
			key: 'actions',
			render: (_, record) => (
				<Space>
					<Tooltip title="Voir les visites">
						<Button
							type="text"
							icon={<EyeOutlined />}
							onClick={() => handleViewVisits(record)}
						/>
					</Tooltip>
					<Tooltip title="Modifier">
						<Button
							type="text"
							icon={<EditOutlined />}
							onClick={() => handleEdit(record)}
						/>
					</Tooltip>
					{record.status === 'active' && (
						<Tooltip title="Annuler le contrat">
							<Button
								type="text"
								danger
								icon={<StopOutlined />}
								onClick={() => handleCancel(record)}
							/>
						</Tooltip>
					)}
					<Tooltip title="Supprimer">
						<Button
							type="text"
							danger
							icon={<DeleteOutlined />}
							onClick={() => handleDelete(record)}
							disabled={record.status === 'active' && record.payment_status === 'paid'}
						/>
					</Tooltip>
				</Space>
			),
			width: 170,
		},
	];

	return (
		<ConfigProvider locale={locale}>
			<div style={{ padding: 24 }}>
				<Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
					<Col>
						<Title level={2} style={{ margin: 0 }}>
							<ScheduleOutlined /> Contrats de Maintenance Programmée
						</Title>
						<Text type="secondary">
							Gestion des contrats avec visites planifiées automatiquement
						</Text>
					</Col>
					<Col>
						<Space>
							<Button icon={<ReloadOutlined />} onClick={() => { loadContracts(); loadUpcomingVisits(); loadRenewalRequests(); }}>
								Actualiser
							</Button>
							<Button type="primary" icon={<PlusOutlined />} onClick={() => {
								setSelectedContract(null);
								form.resetFields();
								setIsModalVisible(true);
							}}>
								Nouveau Contrat
							</Button>
						</Space>
					</Col>
				</Row>

				{/* Demandes de renouvellement */}
				{renewalRequests.length > 0 && (
					<Card
						title={
							<Space>
								<BellOutlined style={{ color: '#faad14' }} />
								<span>Demandes de renouvellement ({renewalRequests.length})</span>
							</Space>
						}
						style={{ marginBottom: 24 }}
						loading={loadingRenewals}
					>
						<List
							dataSource={renewalRequests}
							renderItem={(request) => (
								<List.Item
									actions={[
										<Tooltip title="Approuver" key="approve">
											<Button
												type="primary"
												size="small"
												icon={<CheckOutlined />}
												onClick={() => handleRenewalRequest(request.id, 'approve')}
											>
												Approuver
											</Button>
										</Tooltip>,
										<Tooltip title="Rejeter" key="reject">
											<Button
												danger
												size="small"
												icon={<CloseOutlined />}
												onClick={() => handleRenewalRequest(request.id, 'reject')}
											>
												Rejeter
											</Button>
										</Tooltip>,
									]}
								>
									<List.Item.Meta
										title={
											<Space>
												<Text strong>{request.customerName}</Text>
												<Tag color="blue">{request.contractReference}</Tag>
												{request.subscriptionStatus === 'completed' && (
													<Tag color="green">Contrat terminé</Tag>
												)}
												{request.subscriptionStatus === 'awaiting_second_payment' && (
													<Tag color="orange">En attente de paiement</Tag>
												)}
											</Space>
										}
										description={
											<Space direction="vertical" size={0}>
												{request.equipmentDescription && (
													<Text type="secondary">
														Équipement: {request.equipmentModel} {request.equipmentDescription}
													</Text>
												)}
												{request.customerEmail && (
													<Text type="secondary">Email: {request.customerEmail}</Text>
												)}
												{request.customerPhone && (
													<Text type="secondary">Tél: {request.customerPhone}</Text>
												)}
												<Text type="secondary">
													Demandé le: {dayjs(request.requestedAt).format('DD/MM/YYYY à HH:mm')}
												</Text>
											</Space>
										}
									/>
								</List.Item>
							)}
						/>
					</Card>
				)}

				{/* Carte des visites à venir */}
				<Card 
					title={
						<Space>
							<ClockCircleOutlined />
							Visites à venir (30 jours)
						</Space>
					}
					style={{ marginBottom: 24 }}
					bodyStyle={{ padding: upcomingVisits.length === 0 ? 24 : '12px 24px' }}
					loading={loadingVisits}
				>
					{upcomingVisits.length === 0 ? (
						<Empty description="Aucune visite planifiée dans les 30 prochains jours" />
					) : (
						<div style={{ maxHeight: 250, overflowY: 'auto', paddingRight: 8 }}>
							<List
								dataSource={upcomingVisits}
								renderItem={(visit) => (
									<List.Item>
										<List.Item.Meta
											avatar={<CalendarOutlined style={{ fontSize: 24, color: '#1890ff' }} />}
											title={
												<Space>
													<Text strong>{dayjs(visit.scheduled_date).format('dddd DD MMMM YYYY')}</Text>
													<Tag color="blue">Maintenance programmée</Tag>
												</Space>
											}
											description={
												<Space>
													<Text type="secondary">Client: {getContractCustomerName(visit)}</Text>
													<Text type="secondary">• Équipement: {visit.equipment_name || getEquipmentName(visit.equipment_id)}</Text>
												</Space>
											}
										/>
									</List.Item>
								)}
							/>
						</div>
					)}
				</Card>

				{/* Tableau des contrats */}
				<Card title="Liste des Contrats Programmés">
					<Table
						columns={columns}
						dataSource={contracts}
						rowKey="id"
						loading={loading}
						pagination={{
							showSizeChanger: true,
							showTotal: (total) => `Total: ${total} contrat(s)`,
						}}
					/>
				</Card>

				{/* Modal création/modification de contrat */}
				<Modal
					title={selectedContract ? "Modifier le Contrat" : "Nouveau Contrat de Maintenance"}
					open={isModalVisible}
					onCancel={() => {
						setIsModalVisible(false);
						setSelectedContract(null);
						form.resetFields();
					}}
					footer={null}
					width={700}
				>
					<Form
						form={form}
						layout="vertical"
						onFinish={handleCreate}
						initialValues={{
							visitsTotal: 4,
							visitIntervalMonths: 3,
							durationMonths: 12,
							equipmentCount: 1,
							model: 'LK',
						}}
					>
						<Alert
							message="Contrat programmé"
							description="Les interventions seront planifiées automatiquement selon l'intervalle défini (ex: 4 visites par an, tous les 3 mois)."
							type="info"
							showIcon
							style={{ marginBottom: 16 }}
						/>

						<Divider />

						{!selectedContract && (
							<>
								<Form.Item
									name="customerId"
									label="Client"
									rules={[{ required: true, message: 'Sélectionnez un client' }]}
								>
									<Select
										showSearch
										placeholder="Sélectionner un client"
										optionFilterProp="children"
										filterOption={(input, option) =>
											(option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
										}
									>
										{customers.map((customer: any) => (
											<Option key={customer.id} value={customer.user_id}>
												{`${customer.first_name || customer.user?.first_name || ''} ${customer.last_name || customer.user?.last_name || ''}`.trim() || customer.company_name || `Client #${customer.id}`}
											</Option>
										))}
									</Select>
								</Form.Item>

								<Row gutter={16}>
									<Col span={16}>
										<Form.Item
											name="maintenanceOfferId"
											label="Offre annuelle"
											rules={[{ required: true, message: 'Sélectionnez une offre annuelle' }]}
										>
											<Select
												showSearch
												optionFilterProp="label"
												placeholder={annualOffers.length ? 'Sélectionner une offre' : 'Aucune offre annuelle active'}
												options={annualOffers.map(offer => ({
													value: offer.id,
													label: `${offer.title} — ${Number(offer.price).toLocaleString('fr-FR')} FCFA`
												}))}
											/>
										</Form.Item>
									</Col>
									<Col span={8}>
										<Form.Item
											name="equipmentCount"
											label="Nombre d'équipements"
											rules={[{ required: true, message: 'Quantité requise' }]}
										>
											<InputNumber min={1} max={100} precision={0} style={{ width: '100%' }} />
										</Form.Item>
									</Col>
								</Row>

								{selectedAnnualOffer && (() => {
									const basePrice = selectedAnnualOffer.pricing_mode === 'flat'
										? Number(selectedAnnualOffer.price)
										: Number(selectedAnnualOffer.price) * Number(watchedEquipmentCount);
									const discount = Number(selectedAnnualOffer.annual_discount_percentage || 0);
									const estimatedPrice = Math.max(0, basePrice * (1 - discount / 100));
									return (
										<Alert
											type="success"
											showIcon
											style={{ marginBottom: 16 }}
											message={`${selectedAnnualOffer.visits_total} visite(s) sur ${selectedAnnualOffer.duration_months || selectedAnnualOffer.duration} mois`}
											description={`Tous les ${selectedAnnualOffer.visit_interval_months} mois · remise ${discount}% · estimation ${estimatedPrice.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} FCFA${selectedAnnualOffer.includes_freon ? ' · fréon inclus' : ''}. Le serveur calcule et enregistre le montant définitif.`}
										/>
									);
								})()}
							</>
						)}

						<Row gutter={16}>
							<Col span={12}>
								<Form.Item
									name="equipment"
									label="Équipement"
									rules={[{ required: true, message: 'Entrez l\'équipement' }]}
								>
									<Input placeholder="mural 1 cv" />
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item
									name="model"
									label="Marque"
									rules={[{ required: true, message: 'Sélectionnez une marque' }]}
								>
									<Select placeholder="Sélectionner une marque">
										<Option value="LK">LK</Option>
										<Option value="Carrier">Carrier</Option>
									</Select>
								</Form.Item>
							</Col>
						</Row>

						{!selectedContract && (
							<Form.Item
								name="firstInterventionDate"
								label="Date de la première intervention"
								rules={[{ required: true, message: 'Sélectionnez une date' }]}
							>
								<DatePicker 
									style={{ width: '100%' }} 
									format="DD/MM/YYYY"
									disabledDate={(current) => current && current < dayjs().startOf('day')}
								/>
							</Form.Item>
						)}

						{selectedContract && (selectedContract.offer_snapshot ? (
							<Alert
								type="warning"
								showIcon
								style={{ marginBottom: 16 }}
								message="Conditions commerciales figées"
								description={`Cadence (${selectedContract.visits_total} visites tous les ${selectedContract.visit_interval_months} mois) et prix sont ceux acceptés par le client à la souscription. Pour les changer, modifiez l'offre puis créez un nouveau contrat.`}
							/>
						) : (
							<Row gutter={16}>
								<Col span={8}>
									<Form.Item
										name="visitsTotal"
										label="Nombre de visites"
										rules={[{ required: true }]}
									>
										<InputNumber min={1} max={12} style={{ width: '100%' }} />
									</Form.Item>
								</Col>
								<Col span={8}>
									<Form.Item
										name="visitIntervalMonths"
										label="Intervalle (mois)"
										rules={[{ required: true }]}
									>
										<InputNumber min={1} max={12} style={{ width: '100%' }} />
									</Form.Item>
								</Col>
								<Col span={8}>
									<Form.Item
										name="durationMonths"
										label="Durée contrat (mois)"
										rules={[{ required: true }]}
									>
										<InputNumber min={1} max={24} style={{ width: '100%' }} />
									</Form.Item>
								</Col>
							</Row>
						))}

						<Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
							<Space>
								<Button onClick={() => {
									setIsModalVisible(false);
									form.resetFields();
								}}>
									Annuler
								</Button>
								<Button type="primary" htmlType="submit">
									{selectedContract ? 'Modifier' : 'Créer le contrat'}
								</Button>
							</Space>
						</Form.Item>
					</Form>
				</Modal>

				{/* Modal des visites du contrat */}
				<Modal
					title={
						<Space>
							<CalendarOutlined />
							Visites du Contrat
						</Space>
					}
					open={isVisitsModalVisible}
					onCancel={() => {
						setIsVisitsModalVisible(false);
						setSelectedContract(null);
						setContractVisits([]);
					}}
					footer={null}
					width={700}
				>
					{selectedContract && (
						<>
							<Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
								<Descriptions.Item label="Client">
									{getCustomerName(selectedContract.customer_id)}
								</Descriptions.Item>
								<Descriptions.Item label="Équipement">
									{selectedContract.equipment_description || '-'}
								</Descriptions.Item>
								<Descriptions.Item label="Marque">
									{selectedContract.equipment_model || '-'}
								</Descriptions.Item>
								<Descriptions.Item label="Progression">
									{selectedContract.visits_completed} / {selectedContract.visits_total} visites
								</Descriptions.Item>
								<Descriptions.Item label="Intervalle">
									{selectedContract.visit_interval_months} mois
								</Descriptions.Item>
							</Descriptions>

							<Progress 
								percent={Math.round((selectedContract.visits_completed / selectedContract.visits_total) * 100)} 
								style={{ marginBottom: 16 }}
							/>

							{loadingContractVisits ? (
								<div style={{ textAlign: 'center', padding: 40 }}>
									<Spin />
								</div>
							) : contractVisits.length === 0 ? (
								<Empty description="Aucune visite enregistrée" />
							) : (
								<List
									dataSource={contractVisits}
									renderItem={(visit, index) => {
										const statusColors: Record<string, string> = {
											completed: 'green',
											scheduled: 'blue',
											pending: 'orange',
											in_progress: 'cyan',
											cancelled: 'red',
										};
										const statusIcons: Record<string, React.ReactNode> = {
											completed: <CheckCircleOutlined />,
											scheduled: <ClockCircleOutlined />,
										};
										return (
											<List.Item>
												<List.Item.Meta
													avatar={
														<div style={{
															width: 32,
															height: 32,
															borderRadius: '50%',
															backgroundColor: visit.status === 'completed' ? '#52c41a' : '#1890ff',
															color: 'white',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															fontWeight: 'bold',
														}}>
															{index + 1}
														</div>
													}
													title={
														<Space>
															<Text strong>Visite #{index + 1}</Text>
															<Tag color={statusColors[visit.status] || 'default'} icon={statusIcons[visit.status]}>
																{visit.status === 'completed' ? 'Terminée' : 
																 visit.status === 'scheduled' ? 'Planifiée' :
																 visit.status === 'in_progress' ? 'En cours' :
																 visit.status === 'pending' ? 'En attente' :
																 visit.status}
															</Tag>
														</Space>
													}
													description={
														<Space direction="vertical" size={0}>
															<Text type="secondary">
																Date prévue: {dayjs(visit.scheduled_date).format('dddd DD MMMM YYYY')}
															</Text>
															{visit.completed_at && (
																<Text type="secondary">
																	Terminée le: {dayjs(visit.completed_at).format('DD/MM/YYYY HH:mm')}
																</Text>
															)}
														</Space>
													}
												/>
											</List.Item>
										);
									}}
								/>
							)}
						</>
					)}
				</Modal>
			</div>
		</ConfigProvider>
	);
};

export default ScheduledContractsPage;
